import React, { useMemo, useState } from 'react'
import { ThemeProvider } from '@mui/material'
import { Layers } from 'lucide-react'
import type { PantryMediaFile, SourceFilter } from './types'

// Sentinel for the content_type='video' filter — videos are filtered by
// content_type, not asset_type, so we tag the option with a non-API value
// the onChange handler can route to galleryState.setContentType.
const VIDEO_FILTER_VALUE = '__video__'

const TYPE_FILTER_OPTIONS: { value: string; label: string }[] = [
  { value: '', label: 'All Types' },
  { value: 'product_image', label: 'Product Image' },
  { value: 'brand_logo', label: 'Brand Logo' },
  { value: 'category', label: 'Category' },
  { value: 'banner', label: 'Banner' },
  { value: VIDEO_FILTER_VALUE, label: 'Video' },
]

const SOURCE_OPTIONS: { value: SourceFilter; label: string }[] = [
  { value: 'all', label: 'All Sources' },
  { value: 'breadstack', label: 'Breadstack' },
  { value: 'tenant', label: 'My Uploads' },
]

const tokens = {
  green: '#2563EB',
  greenH: '#1D4ED8',
  greenLight: '#EFF6FF',
  greenMid: '#BFDBFE',
  gray50: '#F9FAFB',
  gray100: '#F3F4F6',
  gray200: '#E5E7EB',
  gray300: '#D1D5DB',
  gray400: '#9CA3AF',
  gray500: '#6B7280',
  gray600: '#4B5563',
  gray700: '#374151',
  gray800: '#1F2937',
  rSm: 6,
  rMd: 10,
}

const theme = { palette: {} }

interface AssetLibraryProps {
  fileSelected: PantryMediaFile[]
  setFileSelected: (files: PantryMediaFile[]) => void
  multiFileSelect?: boolean
  folderSelectedId: string
  setFolderSelectedId: (id: string) => void
  currentImageLength?: number
  galleryState: any
}

const AssetLibrary: React.FC<AssetLibraryProps> = ({
  fileSelected,
  setFileSelected,
  multiFileSelect = false,
  folderSelectedId,
  setFolderSelectedId,
  currentImageLength = 0,
  galleryState,
}) => {
  // search, assetType, contentType, and sourceFilter are owned by galleryState
  // (server-driven via useGalleryState — source toggles `enabled` on each
  // dual-source query, per the project memory rule).
  const search: string = galleryState.search ?? ''
  const assetType: string = galleryState.assetType ?? ''
  const contentType: string = galleryState.contentType ?? ''
  const sourceFilter: SourceFilter = galleryState.sourceFilter ?? 'all'
  // The Type dropdown shows 'Video' when content_type='video' is active,
  // otherwise it reflects the current asset_type filter.
  const typeFilterValue = contentType === 'video' ? VIDEO_FILTER_VALUE : assetType

  const handleTypeFilterChange = (value: string) => {
    if (value === VIDEO_FILTER_VALUE) {
      galleryState.setAssetType('')
      galleryState.setContentType('video')
      return
    }
    if (contentType === 'video') galleryState.setContentType('')
    galleryState.setAssetType(value)
  }
  const files = galleryState.filteredFiles || []
  const isLoading = galleryState.isLoadingFiles
  const isLoadingMore = galleryState.isLoadingMore ?? false
  const hasNextPage = galleryState.hasNextPage ?? false
  const folders = [...galleryState.staticFolders, ...(galleryState.folders || [])]

  const isSelected = (file: PantryMediaFile) =>
    galleryState.isFileSelected(file)

  const handleToggleFile = (file: PantryMediaFile) => {
    galleryState.handleToggleFile(file, multiFileSelect)
  }

  const allFileCount = useMemo(() => {
    const folderTotal = galleryState.folders?.length
      ? galleryState.folders
          .map((f: any) => f.count)
          .reduce((a: number, c: number) => a + c, 0)
      : 0
    return folderTotal + (galleryState.unsortedFileCount || 0)
  }, [galleryState.folders, galleryState.unsortedFileCount])

  const selectedCount =
    fileSelected.filter((f) => f && Object.keys(f).length > 0).length -
    currentImageLength

  return (
    <ThemeProvider theme={theme}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {/* Search bar (full width) */}
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <div style={{ flex: 1, minWidth: 0, position: 'relative' }}>
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke={tokens.gray400}
              strokeWidth="2"
              style={{
                position: 'absolute',
                left: 10,
                top: '50%',
                transform: 'translateY(-50%)',
                width: 14,
                height: 14,
                pointerEvents: 'none',
              }}
            >
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input
              type="text"
              value={search}
              onChange={(e) => galleryState.setSearch(e.target.value)}
              placeholder="Search assets…"
              style={{
                width: '100%',
                boxSizing: 'border-box',
                border: `1px solid ${tokens.gray200}`,
                borderRadius: tokens.rSm,
                padding: '7px 12px 7px 32px',
                fontSize: 13,
                outline: 'none',
                color: tokens.gray800,
                background: '#fff',
              }}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = tokens.green
                e.currentTarget.style.boxShadow = `0 0 0 2px rgba(37,99,235,0.12)`
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = tokens.gray200
                e.currentTarget.style.boxShadow = 'none'
              }}
            />
          </div>
          {selectedCount > 0 && (
            <span
              style={{
                fontSize: 11,
                color: tokens.green,
                fontWeight: 600,
                whiteSpace: 'nowrap',
                background: tokens.greenLight,
                borderRadius: tokens.rSm,
                padding: '4px 8px',
              }}
            >
              {selectedCount} selected
            </span>
          )}
        </div>

        {/* Type filter + divider + Source pills (matches 2026.05.29 Edit Asset modal) */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <select
            value={typeFilterValue}
            onChange={(e) => handleTypeFilterChange(e.target.value)}
            style={{
              border: `1px solid ${tokens.gray200}`,
              borderRadius: tokens.rSm,
              padding: '6px 10px',
              fontSize: 12,
              color: tokens.gray700,
              background: '#fff',
              outline: 'none',
              cursor: 'pointer',
              maxWidth: 160,
            }}
          >
            {TYPE_FILTER_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          <div style={{ width: 1, height: 18, background: tokens.gray200 }} />
          {SOURCE_OPTIONS.map((opt) => {
            const active = sourceFilter === opt.value
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => galleryState.setSourceFilter(opt.value)}
                style={{
                  padding: '4px 10px',
                  fontSize: 11.5,
                  fontWeight: 500,
                  borderRadius: 999,
                  border: `1px solid ${active ? tokens.green : tokens.gray200}`,
                  background: active ? tokens.greenLight : '#fff',
                  color: active ? tokens.green : tokens.gray600,
                  cursor: 'pointer',
                  transition: 'all 0.12s',
                }}
              >
                {opt.label}
              </button>
            )
          })}
        </div>

        {/* Main area: folder sidebar + grid — no inner scroll, the dialog body owns scrolling */}
        <div style={{ display: 'flex', gap: 0, border: `1px solid ${tokens.gray200}`, borderRadius: tokens.rMd, overflow: 'hidden' }}>
          {/* Folder sidebar */}
          <div
            style={{
              display: 'none',
            }}
          >
            <div
              style={{
                fontSize: 10,
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                color: tokens.gray400,
                padding: '4px 12px 8px',
              }}
            >
              Folders
            </div>
            {folders.map((f) => (
              <FolderItem
                key={f.id}
                folder={f}
                selected={folderSelectedId === f.id}
                onClick={() => setFolderSelectedId(f.id)}
                indent={f.level ? (f.level - 1) * 10 : 0}
              />
            ))}
          </div>

          {/* File grid — no overflow:auto here; outer dialog body is the scroller */}
          <div
            style={{
              flex: 1,
              padding: 12,
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
            }}
          >
            {isLoading && files.length === 0 ? (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flex: 1,
                  minHeight: 120,
                  color: tokens.gray400,
                  fontSize: 13,
                  gap: 8,
                }}
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  style={{ width: 20, height: 20, animation: 'spin 1s linear infinite' }}
                >
                  <path d="M21 12a9 9 0 11-6.219-8.56" />
                </svg>
                Loading…
              </div>
            ) : files.length === 0 ? (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flex: 1,
                  minHeight: 120,
                  color: tokens.gray400,
                  gap: 8,
                }}
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  style={{ width: 32, height: 32 }}
                >
                  <rect x="3" y="3" width="18" height="18" rx="2" />
                  <circle cx="8.5" cy="8.5" r="1.5" />
                  <polyline points="21 15 16 10 5 21" />
                </svg>
                <span style={{ fontSize: 13 }}>No images found</span>
              </div>
            ) : (
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(88px, 1fr))',
                  gap: 8,
                }}
              >
                {files.map((file) => (
                  <AssetTile
                    key={file._id || file.id || file.src}
                    file={file}
                    selected={isSelected(file)}
                    active={multiFileSelect && selectedCount > 1 && galleryState.isFileActive(file)}
                    copyState={file._id ? galleryState.spatulaCopies?.[file._id] : undefined}
                    onClick={() => handleToggleFile(file)}
                    onUnselect={() => galleryState.handleUnselectFile(file)}
                  />
                ))}
              </div>
            )}

            {hasNextPage && (
              <div style={{ paddingTop: 4, flexShrink: 0, display: 'flex', justifyContent: 'center' }}>
                <button
                  type="button"
                  onClick={galleryState.handleLoadMore}
                  disabled={isLoadingMore}
                  style={{
                    width: '60%',
                    padding: '6px 20px',
                    borderRadius: tokens.rSm,
                    border: `1px solid ${isLoadingMore ? tokens.gray200 : tokens.gray300}`,
                    background: isLoadingMore ? tokens.gray100 : '#fff',
                    color: isLoadingMore ? tokens.gray400 : tokens.gray700,
                    fontSize: 12,
                    fontWeight: 500,
                    cursor: isLoadingMore ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 6,
                    transition: 'all 0.12s',
                  }}
                  onMouseEnter={(e) => {
                    if (!isLoadingMore) {
                      e.currentTarget.style.borderColor = tokens.green
                      e.currentTarget.style.color = tokens.green
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isLoadingMore) {
                      e.currentTarget.style.borderColor = tokens.gray300
                      e.currentTarget.style.color = tokens.gray700
                    }
                  }}
                >
                  {isLoadingMore && (
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      style={{ width: 12, height: 12, animation: 'spin 1s linear infinite' }}
                    >
                      <path d="M21 12a9 9 0 11-6.219-8.56" />
                    </svg>
                  )}
                  {isLoadingMore ? 'Loading…' : 'Load more'}
                </button>
              </div>
            )}
          </div>
        </div>

        <style>{`
          @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        `}</style>
      </div>
    </ThemeProvider>
  )
}

interface FolderItemProps {
  folder: any
  selected: boolean
  onClick: () => void
  indent?: number
}

const FolderItem: React.FC<FolderItemProps> = ({
  folder,
  selected,
  onClick,
  indent = 0,
}) => (
  <button
    type="button"
    onClick={onClick}
    style={{
      width: '100%',
      display: 'flex',
      alignItems: 'center',
      gap: 6,
      padding: `5px 12px 5px ${12 + indent}px`,
      background: selected ? tokens.greenLight : 'transparent',
      color: selected ? tokens.green : tokens.gray600,
      fontSize: 12.5,
      fontWeight: selected ? 600 : 400,
      border: 'none',
      cursor: 'pointer',
      textAlign: 'left',
      transition: 'background 0.1s',
    }}
    onMouseEnter={(e) => {
      if (!selected) e.currentTarget.style.background = tokens.gray100
    }}
    onMouseLeave={(e) => {
      if (!selected) e.currentTarget.style.background = 'transparent'
    }}
  >
    <svg
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.4"
      style={{ width: 13, height: 13, flexShrink: 0 }}
    >
      <path d="M1.5 4.5h13v9a1 1 0 01-1 1h-11a1 1 0 01-1-1v-9z" />
      <path d="M1.5 4.5v-1a1 1 0 011-1h3.5l1.5 2h-6z" />
    </svg>
    <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
      {folder.name}
    </span>
    <span
      style={{
        fontSize: 10,
        color: selected ? tokens.green : tokens.gray400,
        flexShrink: 0,
      }}
    >
      {folder.count ?? ''}
    </span>
  </button>
)

interface AssetTileProps {
  file: PantryMediaFile
  selected: boolean
  // The picked image whose details are shown, when several are picked
  active?: boolean
  // Uncopied catalog image being copied into Breadstack, or whose copy failed
  copyState?: 'copying' | 'failed'
  onClick: () => void
  onUnselect: () => void
}

const buildSrcSet = (srcset: Record<string, string>): string =>
  Object.entries(srcset)
    .sort(([a], [b]) => Number(a) - Number(b))
    .map(([w, url]) => `${url} ${w}w`)
    .join(', ')

const pickSrc = (srcset: Record<string, string>, targetWidth: number): string => {
  const keys = Object.keys(srcset).map(Number).sort((a, b) => a - b)
  const match = keys.find((k) => k >= targetWidth) ?? keys[keys.length - 1]
  return srcset[String(match)] ?? ''
}

const AssetTile: React.FC<AssetTileProps> = ({
  file,
  selected,
  active = false,
  copyState,
  onClick,
  onUnselect,
}) => {
  const src = file.src || file.public_url || ''
  const hasSrcset = file.srcset && Object.keys(file.srcset).length > 0
  // Uncopied catalog images load from their original host, and some of those
  // links are dead; hide such a tile rather than show a broken image.
  const [broken, setBroken] = useState(false)
  if (broken && file.spatula_doc_id) return null
  return (
    <button
      type="button"
      onClick={onClick}
      title={copyState === 'failed' ? "This image couldn't be added" : file.name}
      style={{
        position: 'relative',
        height: 88,
        borderRadius: tokens.rSm,
        border: `2px solid ${selected ? tokens.green : tokens.gray200}`,
        overflow: 'hidden',
        cursor: 'pointer',
        background: tokens.gray50,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 0,
        transition: 'border-color 0.12s',
        boxShadow: active
          ? `0 0 0 3px ${tokens.green}`
          : selected
            ? `0 0 0 2px rgba(37,99,235,0.2)`
            : 'none',
      }}
      onMouseEnter={(e) => {
        if (!selected)
          (e.currentTarget as HTMLButtonElement).style.borderColor = tokens.green
      }}
      onMouseLeave={(e) => {
        if (!selected)
          (e.currentTarget as HTMLButtonElement).style.borderColor = tokens.gray200
      }}
    >
      {hasSrcset ? (
        <img
          src={pickSrc(file.srcset!, 160)}
          srcSet={buildSrcSet(file.srcset!)}
          sizes="88px"
          alt={file.name}
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          loading="lazy"
        />
      ) : file.content_type?.startsWith('video') ? (
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke={tokens.gray400}
          strokeWidth="1.5"
          style={{ width: 28, height: 28 }}
        >
          <rect x="2" y="6" width="15" height="12" rx="2" />
          <path d="M17 9l5-3v12l-5-3V9z" />
        </svg>
      ) : src ? (
        <img
          src={src}
          alt={file.name}
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          loading="lazy"
          onError={file.spatula_doc_id ? () => setBroken(true) : undefined}
        />
      ) : (
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke={tokens.gray300}
          strokeWidth="1.5"
          style={{ width: 24, height: 24 }}
        >
          <rect x="3" y="3" width="18" height="18" rx="2" />
          <circle cx="8.5" cy="8.5" r="1.5" />
          <polyline points="21 15 16 10 5 21" />
        </svg>
      )}

      <div
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          padding: '3px 5px',
          background: 'linear-gradient(transparent,rgba(0,0,0,0.6))',
          color: '#fff',
          fontSize: 9,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {file.name}
      </div>

      {file._badge === 'breadstack' && (
        <div
          style={{
            position: 'absolute',
            top: 4,
            left: 4,
            width: 16,
            height: 16,
            borderRadius: '50%',
            background: tokens.green,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 1px 3px rgba(0,0,0,.25)',
            pointerEvents: 'none',
            zIndex: 1,
          }}
        >
          <Layers size={9} strokeWidth={2.25} color="#fff" />
        </div>
      )}

      {copyState && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background: copyState === 'failed' ? 'rgba(255,255,255,0.8)' : 'rgba(255,255,255,0.55)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 4,
            textAlign: 'center',
            fontSize: 9.5,
            color: tokens.gray600,
            zIndex: 2,
          }}
        >
          {copyState === 'copying' ? (
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke={tokens.green}
              strokeWidth="2"
              style={{ width: 20, height: 20, animation: 'spin 1s linear infinite' }}
            >
              <path d="M21 12a9 9 0 11-6.219-8.56" />
            </svg>
          ) : (
            "Couldn't add this image"
          )}
        </div>
      )}

      {selected && (
        // A span, not a button: the tile itself is already a button.
        <span
          role="button"
          tabIndex={0}
          title="Unselect"
          aria-label="Unselect"
          onClick={(e) => {
            e.stopPropagation()
            onUnselect()
          }}
          onKeyDown={(e) => {
            if (e.key !== 'Enter' && e.key !== ' ') return
            e.preventDefault()
            e.stopPropagation()
            onUnselect()
          }}
          style={{
            position: 'absolute',
            top: 4,
            right: 4,
            width: 18,
            height: 18,
            background: tokens.green,
            color: '#fff',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 10,
            fontWeight: 700,
            cursor: 'pointer',
            zIndex: 3,
          }}
        >
          ✓
        </span>
      )}
    </button>
  )
}

export default AssetLibrary
